/**
 * Portal Inactive Page (Story 14.3 — Task 9.3 / AC #11 / AC #15)
 *
 * Shown when the meta endpoint reports `status === "inactive"`. The inactive
 * state is always represented by a single generic message — we deliberately
 * do NOT distinguish "not found", "revoked", "expired", or "report deleted"
 * to honor the cross-story timing/privacy contract (Story 14.1 D5 / 14.2 AC #9).
 *
 * UX:
 *   - Centered, calm, single sentence
 *   - NEUTRAL / unbranded (Story 15.7): the tenant is intentionally unknown here
 *     (14.3 AC#15 privacy/timing contract), so we can render NEITHER tenant
 *     branding NOR ProvenLeap — a ProvenLeap wordmark would leak on a paid
 *     tenant's expired link. Fully neutral removes the leak while staying
 *     tenant-agnostic (the only resolution compatible with both 15.7 and 14.3).
 *   - CTA points the viewer back to their agency contact (generic, no tenant)
 *
 * Accessibility:
 *   - <main> landmark with aria-labelledby → <h1>
 *   - Focus management: on mount, the <h1> receives focus so screen readers
 *     announce the generic message (without flashing a raw URL)
 */

import { useEffect, useRef } from "react";

export interface PortalInactivePageProps {
  /**
   * The (constant) inactive reason echoed from the meta response. Accepted
   * for forward-compatibility but NOT rendered — the UX is deliberately
   * reason-agnostic per AC #11 + D18 to honor the cross-story privacy contract.
   */
  reason?: "not_active";
}

export function PortalInactivePage(_props: PortalInactivePageProps = {}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
    const prev = document.title;
    // Neutral title — no ProvenLeap (Story 15.7), no tenant (14.3 AC#15).
    document.title = "Link no longer active";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <main
      id="main-content"
      aria-labelledby="portal-inactive-heading"
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-background text-foreground"
    >
      <div className="w-full max-w-md text-center">
        {/* Story 15.7: NEUTRAL — no ProvenLeap wordmark, no "Powered by" link.
            The tenant is unknown here (14.3 AC#15), so we can render neither
            tenant nor ProvenLeap branding without leaking. */}
        <h1
          id="portal-inactive-heading"
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-semibold tracking-tight text-foreground outline-none"
        >
          This link is no longer active.
        </h1>

        <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
          Contact your agency to request a new link.
        </p>
      </div>
    </main>
  );
}
